//@flow
/**
 * Created by Jamsheed on 03/08/16.
 */

import Immutable, { type Map } from 'immutable';
import _ from 'lodash';
import StringUtils from 'common/utils/StringUtils';

type PointType = {
    degrees: number,
    lat: Function,
    lng: Function
};

type PixelType = {
    x: number,
    y: number
};

type LatLngType = {
    lat: number,
    lng: number
};

class LatLngUtils {
    static base32 = '0123456789bcdefghjkmnpqrstuvwxyz';
    static isValid(latLng: Map<string, number> | LatLngType): boolean {
        if (StringUtils.isEmpty(latLng)) {
            return false;
        }
        let { lat, lng } = latLng;
        if (Immutable.Map.isMap(latLng)) {
            lat = latLng.get('lat');
            lng = latLng.get('lng');
        }
        if (
            StringUtils.isEmpty(lat) ||
            StringUtils.isEmpty(lng) ||
            (lat === 0 && lng === 0)
        ) {
            return false;
        }
        return true;
    }

    static getDistanceBetweenTwoPixels(point1: any, point2: any): number {
        var x1 = point1.x;
        var y1 = point1.y;
        var x2 = point2.x;
        var y2 = point2.y;
        return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    }

    static isLatLng(searchText: string): boolean {
        var latLngRegex = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;
        if (latLngRegex.exec(searchText)) {
            return true;
        } else {
            return false;
        }
    }

    static world2LatLng({ x, y }: PixelType): LatLngType {
        const n = Math.PI - 2 * Math.PI * y;

        // TODO test that this is faster
        // 360 * Math.atan(Math.exp((180 - y * 360) * Math.PI / 180)) / Math.PI - 90;
        return {
            lat:
                (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))),
            lng: x * 360 - 180
        };
    }

    static latLng2World(latLng: LatLngType): any {
        var { lat, lng } = latLng;
        const sin = Math.sin((lat * Math.PI) / 180);
        const x = lng / 360 + 0.5;
        let y = 0.5 - (0.25 * Math.log((1 + sin) / (1 - sin))) / Math.PI;

        y =
            y < -1 // eslint-disable-line
                ? -1
                : y > 1
                ? 1
                : y;
        return {
            x,
            y
        };
    }

    static world2Screen(obj: PixelType, zoom: number): PixelType {
        var { x, y } = obj;
        const scale = Math.pow(2, zoom);
        return {
            x: x * scale * 256, // 256 = 256,
            y: y * scale * 256
        };
    }

    static screen2World(obj: PixelType, zoom: number): PixelType {
        var { x, y } = obj;
        const scale = Math.pow(2, zoom);
        return {
            x: x / (scale * 256), // 256 = 256,
            y: y / (scale * 256)
        };
    }

    static latLngToPixel(latLng: LatLngType, zoom: number): PixelType {
        return LatLngUtils.world2Screen(LatLngUtils.latLng2World(latLng), zoom);
    }

    static pixelToLatLng(pixel: PixelType, zoom: number): LatLngType {
        return LatLngUtils.world2LatLng(LatLngUtils.screen2World(pixel, zoom));
    }

    static getLatLngToPixel(
        bboxMap: DOMRect,
        map: any,
        latLng: LatLngType
    ): PixelType {
        var bbox = bboxMap || map.getDiv().getBoundingClientRect();
        var boundsJson = map.getBounds().toJSON();
        var nw = {
            lat: boundsJson.north,
            lng: boundsJson.west
        };

        nw = LatLngUtils.latLngToPixel(nw, map.getZoom());
        var obj = LatLngUtils.latLngToPixel(latLng, map.getZoom());
        obj = {
            x: obj.x - nw.x + bbox.left,
            y: obj.y - nw.y + bbox.top
        };
        return obj;
    }

    static getPixelToLatLngOffset(
        bboxMap: DOMRect,
        map: any,
        screenPixel: PixelType
    ): LatLngType {
        var bbox = bboxMap || map.getDiv().getBoundingClientRect();
        return LatLngUtils.getPixelToLatLng(bbox, map, {
            x: screenPixel.x + bbox.left,
            y: screenPixel.y + bbox.top
        });
    }
    static getPixelToLatLng(
        bboxMap: DOMRect,
        map: any,
        screenPixel: PixelType
    ): LatLngType {
        var bbox = bboxMap || map.getDiv().getBoundingClientRect();
        var boundsJson = map.getBounds().toJSON();
        var nw = {
            lat: boundsJson.north,
            lng: boundsJson.west
        };

        nw = LatLngUtils.latLngToPixel(nw, map.getZoom());

        var mapPixel = {
            x: nw.x + screenPixel.x - bbox.left,
            y: nw.y + screenPixel.y - bbox.top
        };
        return LatLngUtils.pixelToLatLng(mapPixel, map.getZoom());
    }

    static getBoundsFromLatLngs(latLngs: Array<LatLngType>): any {
        var bounds = new google.maps.LatLngBounds();
        if (!latLngs) {
            return bounds;
        }
        latLngs.forEach(latLng => {
            bounds.extend(new google.maps.LatLng(latLng.lat, latLng.lng));
        });
        bounds = LatLngUtils.extendBounds(bounds);
        return bounds;
    }

    static getBoundsFromPolyline(polyline: string): any {
        const points = new google.maps.geometry.encoding.decodePath(polyline);
        var bounds = new google.maps.LatLngBounds();
        for (var n = 0; n < points.length; n++) {
            bounds.extend(points[n]);
        }
        bounds = LatLngUtils.extendBounds(bounds);
        return bounds;
    }

    static extendBounds(bounds: any): any {
        // ref: http://stackoverflow.com/questions/3334729/google-maps-v3-fitbounds-zoom-too-close-for-single-marker
        // Don't zoom in too far on only one marker
        const northEast = bounds?.getNorthEast();
        const southWest = bounds?.getSouthWest();
        if (northEast && northEast.equals(southWest)) {
            const extendPoint1 = new google.maps.LatLng(
                northEast.lat() + 0.02,
                northEast.lng() + 0.005
            );
            const extendPoint2 = new google.maps.LatLng(
                northEast.lat() - 0.02,
                northEast.lng() - 0.005
            );
            bounds.extend(extendPoint1);
            bounds.extend(extendPoint2);
        }
        return bounds;
        // Adjusting zoom here doesn't work :/
    }

    static distanceBetweenTwoPoints(a: LatLngType, b: LatLngType): number {
        var x1 = a.lat;
        var y1 = a.lng;
        var x2 = b.lat;
        var y2 = b.lng;
        return Math.sqrt(Math.pow(y2 - y1, 2) + Math.pow(x2 - x1, 2));
    }

    static getAngleUsingBearingDifference(a1: number, a2: number): number {
        //  source stackoverflow:-
        // eslint-disable-next-line max-len
        //  http://stackoverflow.com/questions/24045510/how-to-calculate-angle-between-two-path-lines-on-google-map-using-lat-long-coord
        a1 = a1 > 0 ? a1 : 360 + a1; // eslint-disable-line no-param-reassign
        a2 = a2 > 0 ? a2 : 360 + a2; // eslint-disable-line no-param-reassign
        var angle = Math.abs(a1 - a2) + 180;
        if (angle > 180) {
            angle = 360 - angle;
        }
        return Math.abs(angle);
    }

    static getAngleBetweenThreePoints = (
        point1: PointType,
        point2: PointType,
        point3: PointType
    ): number => {
        // This will return angle between line point1-point2 and point2-point3
        var bearing1 = google.maps.geometry.spherical.computeHeading(
            point1,
            point2
        );
        var bearing2 = google.maps.geometry.spherical.computeHeading(
            point2,
            point3
        );
        var angle = LatLngUtils.getAngleUsingBearingDifference(
            bearing1,
            bearing2
        );
        return angle;
    };

    static getPerpendicularDistancefromPointToLine = (
        point: PointType,
        edgePoint: Array<PointType>
    ): number => {
        // This relies on the fact that area of triangle  = 1/2 * base * height
        const [pointA, pointB] = edgePoint;
        const pointsFormingTriangle = [point, pointA, pointB];
        const area = google.maps.geometry.spherical.computeArea(
            pointsFormingTriangle
        );
        const base = google.maps.geometry.spherical.computeDistanceBetween(
            pointA,
            pointB
        );
        const perpendicular = (2 * area) / base;
        return perpendicular;
    };

    static createGeohash(
        latitude: number,
        longitude: number,
        precisionValue: number = 12
    ): string {
        //Src
        //http://www.movable-type.co.uk/scripts/js/latlon-geohash/docs/latlon-geohash.js.html
        let lat = latitude;
        let lon = longitude;
        let precision = precisionValue;

        lat = Number(lat);
        lon = Number(lon);
        precision = Number(precision);
        if (isNaN(lat) || isNaN(lon) || isNaN(precision)) {
            throw new Error('Invalid geohash');
        }

        let idx = 0; // index into base32 map
        let bit = 0; // each char holds 5 bits
        let evenBit = true;
        let geohash = '';

        let latMin = -90;
        let latMax = 90;
        let lonMin = -180;
        let lonMax = 180;

        while (geohash.length < precision) {
            if (evenBit) {
                // bisect E-W longitude
                const lonMid = (lonMin + lonMax) / 2;
                if (lon >= lonMid) {
                    idx = idx * 2 + 1;
                    lonMin = lonMid;
                } else {
                    idx = idx * 2;
                    lonMax = lonMid;
                }
            } else {
                // bisect N-S latitude
                const latMid = (latMin + latMax) / 2;
                if (lat >= latMid) {
                    idx = idx * 2 + 1;
                    latMin = latMid;
                } else {
                    idx = idx * 2;
                    latMax = latMid;
                }
            }
            evenBit = !evenBit;

            if (++bit === 5) {
                // 5 bits gives us a character: append it and start over
                geohash += LatLngUtils.base32.charAt(idx);
                bit = 0;
                idx = 0;
            }
        }

        return geohash;
    }

    static getDistanceBasedScore = (
        pointC: PointType,
        edgePoint: PointType
    ): number => {
        const [pointA, pointB] = edgePoint;
        const angleBetweenCAAndAB = LatLngUtils.getAngleBetweenThreePoints(
            pointC,
            pointA,
            pointB
        );
        const angleBetweenCBAndBA = LatLngUtils.getAngleBetweenThreePoints(
            pointC,
            pointB,
            pointA
        );
        let score;
        // if angle is greater than 90 degree, it can not have projection on line segment
        // Hence giving it worst score
        if (angleBetweenCAAndAB > 90 || angleBetweenCBAndBA > 90) {
            score = Number.MAX_SAFE_INTEGER;
        } else {
            score = LatLngUtils.getPerpendicularDistancefromPointToLine(
                pointC,
                edgePoint
            );
        }
        return score;
    };

    static getEdgeWithLeastScore = (
        edges: Array<{ edgePoint: Array<PointType>, score: number }>
    ): { edgePoint: Array<PointType>, score: number } => {
        return _.minBy(edges, edge => {
            return edge.score;
        });
    };

    static snapToNearestPoint(
        points: Array<PointType>,
        point: PointType
    ): ?PointType {
        if (!points) {
            return null;
        }
        return _.minBy(points, pivotPoint => {
            return LatLngUtils.distanceBetweenTwoPoints(
                point.toJSON(),
                pivotPoint.toJSON()
            );
        });
    }

    static getSnappedPoint = (
        polyline: string,
        latLng: LatLngType
    ): ?PointType => {
        if (polyline) {
            const path = google.maps.geometry.encoding.decodePath(polyline);
            const point = new google.maps.LatLng(latLng.lat, latLng.lng);
            let filteredEdges = [];
            for (let j = 0; j < path.length - 1; j++) {
                const first = path[j];
                const second = path[j + 1];
                const deltaY = (first.lat() - second.lat()) * 100000;
                const deltaX = -(first.lng() - second.lng()) * 100000;
                const radians = Math.atan2(deltaY, deltaX);
                const degrees = (radians * 180) / Math.PI;
                first.degrees = degrees;
                const edgePoints = [first, second];
                const edge = new google.maps.Polyline({
                    path: edgePoints
                });

                // 10e-4 -> 10e-3.
                // Making this more lenient, as we were missing good polylines sometimes, because of this threshold.
                // Example:
                /*
                google.com/maps/d/viewer?mid=1NL99c6NnvFybM9q0ph4nSPxAyDw&ll=13.072592701049016%2C77.59331500663757&z=15
                */

                var isOnLine = google.maps.geometry.poly.isLocationOnEdge(
                    point,
                    edge,
                    10e-3
                );
                if (isOnLine) {
                    filteredEdges.push(edgePoints);
                }
            }
            filteredEdges = filteredEdges.map(edgePoint => {
                return {
                    edgePoint,
                    score: LatLngUtils.getDistanceBasedScore(point, edgePoint)
                };
            });
            if (filteredEdges && filteredEdges.length > 0) {
                const edgeWithMinScore = LatLngUtils.getEdgeWithLeastScore(
                    filteredEdges
                );
                if (!edgeWithMinScore) {
                    return null;
                }
                const snappedPoint = LatLngUtils.snapToNearestPoint(
                    edgeWithMinScore.edgePoint,
                    point
                );
                return snappedPoint;
            }
        }
        return null;
    };
}

export default LatLngUtils;
